import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const VerificarEmailPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'already_verified', 'error'
  const [message, setMessage] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [estadoActual, setEstadoActual] = useState('');

  useEffect(() => {
    verificarEmail();
  }, [token]);

  const verificarEmail = async () => {
    try {
      const response = await api.get(`/solicitudes/suscripcion/verificar/${token}`);
      
      // Verificar si ya estaba verificado
      if (response.data.ya_verificado) {
        setStatus('already_verified');
        setEstadoActual(response.data.estado_actual);
      } else {
        setStatus('success');
      }
      setMessage(response.data.message);
      setRazonSocial(response.data.razon_social);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'El enlace de verificación es inválido o ha expirado.');
    }
  };

  // Función para obtener texto legible del estado
  const getEstadoTexto = (estado) => {
    const estados = {
      'pendiente': 'En revisión',
      'aprobada': 'Aprobada ✓',
      'rechazada': 'Rechazada'
    };
    return estados[estado] || estado;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-900 via-teal-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <div className="space-y-4">
              <Loader2 className="h-16 w-16 text-teal-500 mx-auto animate-spin" />
              <h2 className="text-xl font-semibold text-gray-800">Verificando tu email...</h2>
              <p className="text-gray-500">Por favor espera un momento</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">¡Email Verificado!</h2>
                {razonSocial && (
                  <p className="text-teal-600 font-medium mb-2">{razonSocial}</p>
                )}
                <p className="text-gray-600">{message}</p>
              </div>
              
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-left">
                <h3 className="font-medium text-teal-800 mb-2">¿Qué sigue?</h3>
                <ul className="text-sm text-teal-700 space-y-1">
                  <li>✓ Nuestro equipo revisará tu solicitud</li>
                  <li>✓ Recibirás un email con la confirmación</li>
                  <li>✓ Te enviaremos tus credenciales de acceso</li>
                </ul>
              </div>

              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700">
                <Link to="/">
                  Ir al Inicio
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          )}

          {/* Nuevo estado: Ya verificado anteriormente */}
          {status === 'already_verified' && (
            <div className="space-y-6">
              <div className="bg-amber-100 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
                <AlertCircle className="h-12 w-12 text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Email ya verificado</h2>
                {razonSocial && (
                  <p className="text-teal-600 font-medium mb-2">{razonSocial}</p>
                )}
                <p className="text-gray-600">{message}</p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h3 className="font-medium text-blue-800 mb-2">Estado de tu solicitud</h3>
                <p className="text-sm text-blue-700">
                  Tu solicitud se encuentra: <span className={`font-semibold ${
                    estadoActual === 'aprobada' ? 'text-green-600' : 
                    estadoActual === 'rechazada' ? 'text-red-600' : 
                    'text-amber-600'
                  }`}>
                    {getEstadoTexto(estadoActual)}
                  </span>
                </p>
                {estadoActual === 'pendiente' && (
                  <p className="text-sm text-blue-600 mt-2">
                    Nuestro equipo está revisando tu solicitud. Recibirás un email cuando sea procesada.
                  </p>
                )}
                {estadoActual === 'aprobada' && (
                  <p className="text-sm text-green-600 mt-2">
                    ¡Felicitaciones! Ya puedes ingresar a la plataforma con las credenciales que te enviamos por email.
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button asChild variant="outline" className="flex-1">
                  <Link to="/">Ir al Inicio</Link>
                </Button>
                {estadoActual === 'aprobada' && (
                  <Button asChild className="flex-1 bg-teal-600 hover:bg-teal-700">
                    <Link to="/login">
                      Iniciar Sesión
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Verificación Fallida</h2>
                <p className="text-gray-600">{message}</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                <h3 className="font-medium text-amber-800 mb-2">¿Necesitas ayuda?</h3>
                <p className="text-sm text-amber-700">
                  Si crees que esto es un error, puedes intentar solicitar una nueva suscripción 
                  o contactarnos a <a href="mailto:soporte@cmpublicaciones.com" className="underline">soporte@cmpublicaciones.com</a>
                </p>
              </div>

              <div className="flex gap-3">
                <Button asChild variant="outline" className="flex-1">
                  <Link to="/">Ir al Inicio</Link>
                </Button>
                <Button asChild className="flex-1 bg-teal-600 hover:bg-teal-700">
                  <Link to="/suscripcion">
                    <Mail className="h-4 w-4 mr-2" />
                    Nueva Solicitud
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerificarEmailPage;
